import { useMemo } from 'react'
import type { ShiftsRow } from 'modules/shift_management/lib/types'
import {
  type ExtendedGridEventAppearance,
  transformShiftToEvent,
  transformLeaveToEvent,
  transformRestDayToEvent,
  transformHolidayToEvent,
  transformTimeOffSummaryToEvent
} from 'modules/shift_management/utils/TimeGridTable/transformEvents'
import type { View } from 'modules/shift_management/lib/types'
import ShiftsConflicts from 'modules/shift_management/store/internal/collections/ShiftsConflicts'
import type { TimeOffSummaryOptions } from 'modules/shift_management/hooks/gridTable/useBuildTimeOffSummary'
interface UseBuildEventsOptions {
  shiftsRows: ShiftsRow[]
  view: View
  shiftsConflicts: ShiftsConflicts
  timeOffSummary?: TimeOffSummaryOptions
}

export const useBuildEvents = ({
  shiftsRows = [],
  view,
  shiftsConflicts,
  timeOffSummary
}: UseBuildEventsOptions): ExtendedGridEventAppearance[] => {
  return useMemo(() => {
    const events: ExtendedGridEventAppearance[] = []
    shiftsRows.forEach((row) => {
      const restDaysInRange = row.restDays?.filterByRange(view.firstDay, view.lastDay) ?? []

      const leaves = row.leaves.leaves ?? []
      const restDays = restDaysInRange.restDays ?? []
      const shifts = row.shifts.shifts ?? []
      const holidays = row.holidays.holidays ?? []
      const entityId = row.key

      shifts.forEach((shift) => {
        events.push(transformShiftToEvent(shift, entityId, shiftsConflicts, view))
      })

      leaves.forEach((leave) => {
        events.push(transformLeaveToEvent(leave, entityId))
      })

      restDays.forEach((restDay) => {
        events.push(transformRestDayToEvent(restDay, entityId))
      })

      holidays.forEach((holiday) => {
        events.push(transformHolidayToEvent(holiday, entityId))
      })
    })

    if (timeOffSummary) {
      const summaryEvents = timeOffSummary.slots.map((slot) =>
        transformTimeOffSummaryToEvent({
          slotIndex: slot.index,
          entityId: timeOffSummary.entityId,
          start: slot.start,
          end: slot.end,
          absencesCount: timeOffSummary.getAbsencesCountAt(slot.start),
          publishedRestDaysCount: timeOffSummary.getPublishedRestDaysCountAt(slot.start),
          loading: timeOffSummary.loading,
          onClick: () => timeOffSummary.onClick(slot.start),
        })
      )
      events.push(...summaryEvents)
    }

    return events
  }, [shiftsRows, shiftsConflicts, timeOffSummary, view])
}
